import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { useOwnerQa } from '../contexts/OwnerQaContext';
import { useLanguage } from '../contexts/LanguageContext';
import ResponsiveAppMenu from '../components/ResponsiveAppMenu';
import { trackUsageEvent } from '../analytics/UsageAnalytics';
import {
  puzzleTrainingRequest, trainingCheckoutToken,
} from './dailyPuzzleApi';
import './puzzle-offer.css';

const CONTENT = {
  en: {
    eyebrow: 'YOUR PERSONAL CHESS PUZZLE COACH',
    greeting: "Hi! I'm Hood.",
    title: 'You play. Hood finds your next challenge.',
    subtitle: 'Chess puzzles picked for your level. Your results guide what comes next.',
    bubble: 'Give me a few moves. I will learn what you need next!',
    seven: '7 free puzzles every day', used: 'used today', reset: 'Resets at midnight UTC',
    unlimited: 'Unlimited training active', freeButton: "Let's train", fullButton: 'Keep training',
    guestButton: 'Start my free week',
    signInNote: '7 days of unlimited puzzles from registration. No card needed.',
    existingAccount: 'Already registered? Sign in',
    trialOffer: 'Your first 7 days: unlimited puzzles, free',
    afterTrial: 'After your free week: 7 free puzzles every day, or £0.90/month for unlimited puzzles.',
    trialActive: 'Your free week is active',
    trialRemaining: (days) => `${days} ${days === 1 ? 'day' : 'days'} left · Unlimited puzzles`,
    checking: 'Checking your daily puzzles…',
    firstSteps: ['Tell Hood your level', 'Hood chooses the theme AND the position', 'You solve the puzzle', 'Hood remembers and adapts'],
    stepsDetail: [
      'A quick starting point. Hood adjusts as you play.',
      'From millions of positions, Hood searches for a suitable task at your current level.',
      'Practise one chess pattern at a time, then combine themes as you improve.',
      'Your results are saved. The next position follows your strengths and weaknesses.',
    ],
    why: 'What makes Chesshood different?',
    whyCopy: "Choosing the right positions for a student is one of a human chess coach's most important jobs. Hood's AI takes on that part of coaching: it uses your level, category ratings and results to choose what you need to practise and find suitable positions. You focus on learning; Hood adapts the next tasks as you progress.",
    manual: 'Explore the training map (optional)',
    benefits: ['Unlimited puzzles for a full month', 'AI selects categories that need practice', 'Positions matched to your current level', 'Next tasks adapt to your results', 'Your progress is saved'],
    offerEyebrow: 'CHESSHOOD PUZZLE TRAINING',
    offerTitle: 'Unlock unlimited practice',
    offerPrice: '/ month',
    offerBody: "A full month of unlimited puzzles, with Hood's AI choosing what to practise next.",
    coachTitle: 'The right positions make the difference',
    coachBody: "Finding suitable positions is a key part of a chess coach's work. Hood's AI does that selection for you, matching the category and difficulty to your level and results.",
    freeReturnTitle: '7 more free puzzles tomorrow',
    freeReturnBody: 'Come back tomorrow at this time for 7 more free puzzles.',
    payNow: 'Want to keep training now? Pay £0.90 for a full month of unlimited puzzles.',
    offerButton: 'Unlock for £0.90/month',
    cancel: 'Paid membership renews monthly. Cancel anytime.',
    only: 'Puzzle training only. Hood AI chat and game analysis are not included.',
    manage: 'Manage membership', login: 'Sign in to keep your progress',
    exhausted: "You've used your 7 free puzzles today. Come back tomorrow at this time for 7 more, or pay £0.90 now for a full month of unlimited puzzles.",
    error: 'We could not check your training access. Please try again.',
    checkoutError: 'We could not open the payment page. Please try again.',
    retry: 'Refresh access',
    processing: 'Opening secure checkout…',
    success: 'Thanks! We are confirming your subscription. Refresh if it does not appear immediately.',
    cancelled: 'No payment was taken. Your free training remains available.',
    active: 'You already have unlimited puzzle training.',
  },
  pl: {
    eyebrow: 'TWÓJ OSOBISTY TRENER SZACHOWY',
    greeting: 'Cześć! Jestem Hood.',
    title: 'Ty grasz. Hood wybiera kolejne wyzwanie.',
    subtitle: 'Zadania dopasowane do Twojego poziomu. Twoje wyniki wyznaczają kolejny krok.',
    bubble: 'Pokaż mi kilka ruchów. Dowiem się, czego potrzebujesz!',
    seven: '7 darmowych puzzli każdego dnia', used: 'wykorzystane dzisiaj', reset: 'Reset codziennie o 00:00 UTC',
    unlimited: 'Trening bez limitu aktywny', freeButton: 'Zacznij trening', fullButton: 'Kontynuuj trening',
    guestButton: 'Zacznij darmowy tydzień',
    signInNote: '7 dni puzzli bez limitu od rejestracji. Bez podawania karty.',
    existingAccount: 'Masz już konto? Zaloguj się',
    trialOffer: 'Pierwsze 7 dni: puzzle bez limitu za darmo',
    afterTrial: 'Po darmowym tygodniu: 7 darmowych puzzli dziennie lub £0.90 miesięcznie bez limitu.',
    trialActive: 'Twój darmowy tydzień jest aktywny',
    trialRemaining: (days) => `Pozostało: ${days} ${days === 1 ? 'dzień' : 'dni'} · Puzzle bez limitu`,
    checking: 'Sprawdzam dzisiejszy limit zadań…',
    firstSteps: ['Podajesz swój poziom', 'Hood wybiera motyw i pozycję', 'Rozwiązujesz puzzle', 'Hood zapamiętuje i dopasowuje'],
    stepsDetail: [
      'To tylko punkt startowy. Hood dopasowuje trudność po kolejnych zadaniach.',
      'Spośród milionów pozycji Hood szuka odpowiedniego zadania na teraz.',
      'Uczysz się wzorców kategoriami, a potem łączysz różne motywy.',
      'Wyniki są zapisywane, a kolejne zadania dopasowują się do Twoich słabości.',
    ],
    why: 'Dlaczego Chesshood działa inaczej?',
    whyCopy: 'Dobór odpowiednich pozycji do nauki to jedno z najważniejszych zadań trenera szachowego. Sztuczna inteligencja Hooda wykonuje tę część pracy: na podstawie Twojego poziomu, rankingów w kategoriach i wyników wybiera, co warto ćwiczyć, oraz dopasowane pozycje. Ty skupiasz się na nauce, a Hood dostosowuje kolejne zadania do Twoich postępów.',
    manual: 'Zobacz mapę treningu (opcjonalnie)',
    benefits: ['Puzzle bez limitu przez cały miesiąc', 'AI wybiera kategorie, które warto ćwiczyć', 'Pozycje dopasowane do Twojego poziomu', 'Kolejne zadania dostosowane do wyników', 'Twoje postępy są zapisywane'],
    offerEyebrow: 'CHESSHOOD · TRENING PUZZLI',
    offerTitle: 'Odblokuj trening bez limitu',
    offerPrice: '/ miesiąc',
    offerBody: 'Cały miesiąc puzzli bez limitu. Sztuczna inteligencja Hooda dobiera, co warto ćwiczyć dalej.',
    coachTitle: 'Odpowiednie pozycje robią różnicę',
    coachBody: 'Szukanie właściwych pozycji to ważna część pracy trenera szachowego. AI Hooda robi to za Ciebie: dopasowuje kategorię i trudność do Twojego poziomu oraz wyników.',
    freeReturnTitle: 'Jutro kolejne 7 darmowych puzzli',
    freeReturnBody: 'Wróć jutro o tej samej porze po kolejne 7 darmowych puzzli.',
    payNow: 'Chcesz trenować dalej już teraz? Zapłać £0.90 za cały miesiąc puzzli bez limitu.',
    offerButton: 'Odblokuj za £0.90/mies.',
    cancel: 'Płatny abonament odnawia się co miesiąc. Anuluj kiedy chcesz.',
    only: 'Tylko trening puzzli. Czat Hood AI i analiza partii nie są wliczone.',
    manage: 'Zarządzaj abonamentem', login: 'Zaloguj się, aby zachować postępy',
    exhausted: 'Wykorzystałeś dziś 7 darmowych puzzli. Wróć jutro o tej samej porze po kolejne 7 lub zapłać teraz £0.90 za cały miesiąc puzzli bez limitu.',
    error: 'Nie udało się sprawdzić dostępu. Spróbuj ponownie.',
    checkoutError: 'Nie udało się otworzyć strony płatności. Spróbuj ponownie.',
    retry: 'Odśwież dostęp',
    processing: 'Otwieram bezpieczną płatność…',
    success: 'Dziękujemy! Potwierdzamy abonament. Odśwież, jeśli jeszcze go nie widać.',
    cancelled: 'Nie pobrano płatności. Darmowy trening nadal jest dostępny.',
    active: 'Masz już nielimitowany trening puzzli.',
  },
  es: {
    eyebrow: 'TU ENTRENADOR PERSONAL DE AJEDREZ',
    greeting: '¡Hola! Soy Hood.',
    title: 'Tú juegas. Hood elige tu próximo reto.',
    subtitle: 'Puzzles adaptados a tu nivel. Tus resultados guían el siguiente paso.',
    bubble: 'Haz unas jugadas. ¡Aprenderé lo que necesitas!',
    seven: '7 puzzles gratis cada día', used: 'usados hoy', reset: 'Se reinicia a las 00:00 UTC',
    unlimited: 'Entrenamiento ilimitado activo', freeButton: 'Empezar a entrenar', fullButton: 'Seguir entrenando',
    guestButton: 'Empezar mi semana gratis',
    signInNote: '7 días de puzzles ilimitados desde el registro. Sin tarjeta.',
    existingAccount: '¿Ya tienes cuenta? Inicia sesión',
    trialOffer: 'Tus primeros 7 días: puzzles ilimitados gratis',
    afterTrial: 'Después de tu semana gratis: 7 puzzles gratis cada día, o £0.90/mes sin límites.',
    trialActive: 'Tu semana gratis está activa',
    trialRemaining: (days) => `${days} ${days === 1 ? 'día restante' : 'días restantes'} · Puzzles ilimitados`,
    checking: 'Comprobando tus puzzles de hoy…',
    firstSteps: ['Indica tu nivel', 'Hood elige el tema Y la posición', 'Resuelves el puzzle', 'Hood aprende y se adapta'],
    stepsDetail: [
      'Solo como punto de partida. Hood adapta la dificultad con tus resultados.',
      'Entre millones de posiciones, Hood encuentra el ejercicio adecuado para ti.',
      'Aprendes patrones por temas y después combinas ideas más complejas.',
      'Se guardan tus resultados y Hood adapta los próximos ejercicios.',
    ],
    why: '¿Qué hace diferente a Chesshood?',
    whyCopy: 'Elegir las posiciones adecuadas para cada alumno es una de las tareas más importantes de un entrenador de ajedrez. La IA de Hood realiza esa parte del trabajo: utiliza tu nivel, tus puntuaciones por categoría y tus resultados para elegir qué necesitas practicar y encontrar posiciones adecuadas. Tú te concentras en aprender y Hood adapta los próximos ejercicios a tu progreso.',
    manual: 'Explora el mapa de entrenamiento (opcional)',
    benefits: ['Puzzles ilimitados durante todo un mes', 'La IA elige categorías que necesitas practicar', 'Posiciones adaptadas a tu nivel actual', 'Los siguientes ejercicios se adaptan a tus resultados', 'Tu progreso queda guardado'],
    offerEyebrow: 'CHESSHOOD · ENTRENAMIENTO DE PUZZLES',
    offerTitle: 'Desbloquea entrenamiento ilimitado',
    offerPrice: '/ mes',
    offerBody: 'Todo un mes de puzzles ilimitados, con la IA de Hood eligiendo qué practicar después.',
    coachTitle: 'Las posiciones adecuadas marcan la diferencia',
    coachBody: 'Buscar posiciones adecuadas es una parte importante del trabajo de un entrenador de ajedrez. La IA de Hood las selecciona para ti, adaptando la categoría y la dificultad a tu nivel y tus resultados.',
    freeReturnTitle: 'Mañana, 7 puzzles gratis más',
    freeReturnBody: 'Vuelve mañana a esta misma hora para resolver otros 7 puzzles gratis.',
    payNow: '¿Quieres seguir entrenando ahora? Paga £0.90 por todo un mes de puzzles ilimitados.',
    offerButton: 'Desbloquear por £0.90/mes',
    cancel: 'La suscripción de pago se renueva mensualmente. Cancela cuando quieras.',
    only: 'Solo entrenamiento de puzzles. No incluye el chat Hood AI ni análisis de partidas.',
    manage: 'Gestionar suscripción', login: 'Inicia sesión para guardar tu progreso',
    exhausted: 'Has usado tus 7 puzzles gratis de hoy. Vuelve mañana a esta misma hora para resolver otros 7 o paga ahora £0.90 por todo un mes de puzzles ilimitados.',
    error: 'No pudimos comprobar tu acceso. Inténtalo otra vez.',
    checkoutError: 'No pudimos abrir la página de pago. Inténtalo otra vez.',
    retry: 'Actualizar acceso',
    processing: 'Abriendo pago seguro…',
    success: 'Gracias. Estamos confirmando tu suscripción. Actualiza si aún no aparece.',
    cancelled: 'No se ha cobrado nada. Tus puzzles gratis siguen disponibles.',
    active: 'Ya tienes acceso ilimitado a los puzzles.',
  },
};

const ICONS = ['♟', '♛', '♞', '★'];

export default function PuzzleTrainingLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const { firebaseUser, loading: authLoading } = useContext(UserContext);
  const qa = useOwnerQa();
  const puzzleQaMode = qa.isOwner && qa.active ? qa.config.puzzleTrainingMode : 'real';
  const testingPuzzleMembership = puzzleQaMode !== 'real';
  const { language } = useLanguage();
  const copy = CONTENT[language] || CONTENT.en;
  const params = new URLSearchParams(location.search);
  const checkout = params.get('checkout') || '';
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [billingError, setBillingError] = useState('');
  const accessRequest = useRef(0);
  const accessKey = firebaseUser ? `${firebaseUser.uid}:${puzzleQaMode}` : '';
  const currentAccess = access?.key === accessKey ? access.data : null;

  const refreshAccess = async () => {
    const request = ++accessRequest.current;
    setAccess(null);
    setError('');
    if (!firebaseUser) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await puzzleTrainingRequest('access', {
        params: {
          ...(checkout && !testingPuzzleMembership ? { checkout } : {}),
          ...(testingPuzzleMembership ? { qaMode: puzzleQaMode } : {}),
        },
      });
      if (request === accessRequest.current) setAccess({ key: accessKey, data });
    } catch {
      if (request === accessRequest.current) setError(copy.error);
    } finally {
      if (request === accessRequest.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!testingPuzzleMembership) {
      trackUsageEvent('puzzle_training_offer_view', { meta: { version: 'seven_daily_90p' } });
    }
  }, [testingPuzzleMembership]);

  useEffect(() => {
    setBillingError('');
    if (!authLoading) refreshAccess();
    return () => { accessRequest.current += 1; };
    // Access is tied to the verified account and checkout redirect.
  }, [firebaseUser?.uid, authLoading, checkout, puzzleQaMode]);

  const unlimited = testingPuzzleMembership
    ? puzzleQaMode === 'pro'
    : Boolean(firebaseUser && currentAccess?.unlimited);
  const trialActive = !testingPuzzleMembership && unlimited && Boolean(currentAccess?.trialActive);
  const accessReady = !authLoading && !loading && (!firebaseUser || Boolean(currentAccess));
  const used = currentAccess?.usedToday || 0;
  const remaining = currentAccess?.remainingToday ?? 7;
  const limited = Boolean(currentAccess && !unlimited && remaining <= 0);
  // A bookmarked paywall URL is navigation intent, not proof of today's quota.
  const dailyLimitMessage = limited;
  const showMembership = params.get('dailyLimit') === '1';

  useEffect(() => {
    if (showMembership && accessReady && !unlimited) {
      document.getElementById('puzzle-offer-membership')?.scrollIntoView({ block: 'start' });
    }
  }, [showMembership, accessReady, unlimited]);

  const beginTraining = () => {
    if (!accessReady) return;
    if (!testingPuzzleMembership) {
      trackUsageEvent('puzzle_training_start_click', { meta: { remaining, unlimited } });
    }
    if (limited) {
      document.getElementById('puzzle-offer-membership')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (!firebaseUser) {
      navigate('/register', { state: { from: { pathname: '/play' } } });
      return;
    }
    navigate('/play');
  };

  const subscribe = async () => {
    setBillingError('');
    if (!testingPuzzleMembership) {
      trackUsageEvent('puzzle_training_checkout_click', { meta: { amountPence: 90, source: 'real' } });
    }
    if (testingPuzzleMembership) {
      // Never open live Stripe when testing; only change the local QA scenario.
      qa.setPuzzleTrainingMode('pro');
      setError('');
      return;
    }
    if (!firebaseUser) {
      navigate('/login', { state: { from: { pathname: '/training' } } });
      return;
    }
    setBusy(true); setError('');
    try {
      const result = await puzzleTrainingRequest('checkout', {
        body: { requestId: trainingCheckoutToken(firebaseUser.uid) },
      });
      if (result.url) window.location.assign(result.url);
      else throw new Error('checkout_url_missing');
    } catch (err) {
      setBillingError(err.message === 'already_subscribed' ? copy.active : copy.checkoutError);
      setBusy(false);
      refreshAccess();
    }
  };

  const manage = async () => {
    setBusy(true); setBillingError('');
    try {
      const result = await puzzleTrainingRequest('portal', { body: {} });
      if (result.url) window.location.assign(result.url);
      else throw new Error('portal_url_missing');
    } catch {
      setBillingError(copy.checkoutError);
      setBusy(false);
    }
  };

  return (
    <main className="puzzle-offer" style={{ backgroundImage: "linear-gradient(180deg, rgba(18,75,125,.25), rgba(9,38,75,.70)), url('/trainingtlo.png')" }}>
      <div className="puzzle-offer-container">
        <header className="puzzle-offer-header">
          <Link to="/" className="puzzle-offer-logo"><img src="/hood.png" alt="" /> Chesshood</Link>
          <ResponsiveAppMenu />
        </header>

        <section className="puzzle-offer-hero">
          <div className="puzzle-offer-hero-copy">
            <p className="puzzle-offer-eyebrow">{copy.eyebrow}</p>
            <h1>{copy.title}</h1>
            <p className="puzzle-offer-summary">{copy.subtitle}</p>
            {testingPuzzleMembership && (
              <p className="puzzle-offer-limit" role="status">
                🧪 Owner QA · {puzzleQaMode === 'free' ? 'FREE (7 daily puzzles)' : 'SUBSCRIBED (£0.90 simulation)'} · No real payment
              </p>
            )}
            <div className="puzzle-offer-today" aria-live="polite">
              <div className="puzzle-offer-today-title">{!firebaseUser ? copy.trialOffer : trialActive ? copy.trialActive : unlimited ? copy.unlimited : copy.seven}</div>
              {trialActive && <small>{copy.trialRemaining(currentAccess.trialDaysLeft)}</small>}
              {(!firebaseUser || trialActive) && <small>{copy.afterTrial}</small>}
              {!unlimited && firebaseUser && accessReady && (
                <>
                  <div className="puzzle-offer-dots" aria-label={used + ' of 7 puzzles used today'}>
                    {Array.from({ length: 7 }, (_, index) => (
                      <span key={index} className={index < used ? 'puzzle-offer-dot-done' : ''} />
                    ))}
                  </div>
                  <small>{used}/7 {copy.used} · {copy.reset}</small>
                </>
              )}
              {!accessReady && !error && <small role="status">{copy.checking}</small>}
            </div>
            {dailyLimitMessage && <p className="puzzle-offer-limit" role="status">{copy.exhausted}</p>}
            {(error || billingError) && <p className="puzzle-offer-limit" role="alert">{error || billingError}</p>}
            <div className="puzzle-offer-buttons">
              <button type="button" className="puzzle-offer-button" onClick={beginTraining} disabled={!accessReady}>
                ▶ {!firebaseUser ? copy.guestButton : unlimited ? copy.fullButton : copy.freeButton}
              </button>
              {error && <button type="button" className="puzzle-offer-text-button" onClick={refreshAccess}>{copy.retry}</button>}
            </div>
            {!firebaseUser && (
              <p className="puzzle-offer-signin-note">
                {copy.signInNote}{' '}<Link to="/login" state={{ from: { pathname: '/play' } }}>{copy.existingAccount}</Link>
              </p>
            )}
          </div>
          <div className="puzzle-offer-hood-scene">
            <div className="puzzle-offer-bubble"><strong>{copy.greeting}</strong><span>{copy.bubble}</span></div>
            <img src="/gamefication/Hoodsuccess.png" alt="Hood, your Chesshood chess pawn coach" className="puzzle-offer-hood" />
          </div>
        </section>

        <section className="puzzle-offer-journey" aria-label="How Hood trains you">
          {copy.firstSteps.map((step, index) => (
            <div className="puzzle-offer-step" key={step}>
              <span className="puzzle-offer-step-icon" aria-hidden="true">{ICONS[index]}</span>
              <span className="puzzle-offer-step-number">0{index + 1}</span>
              <h2>{step}</h2>
              <p>{copy.stepsDetail[index]}</p>
            </div>
          ))}
        </section>

        <section className="puzzle-offer-why">
          <img src="/hood.png" alt="" />
          <div><h2>{copy.why}</h2><p>{copy.whyCopy}</p></div>
        </section>
        <div className="puzzle-offer-manual"><Link to="/entrytraining">{copy.manual} ↗</Link></div>

        <section className="puzzle-offer-membership" id="puzzle-offer-membership">
          {!unlimited && (
            <aside className="puzzle-offer-return" aria-labelledby="puzzle-offer-return-title">
              <h3 id="puzzle-offer-return-title">{firebaseUser ? copy.freeReturnTitle : copy.trialOffer}</h3>
              <p>{firebaseUser ? copy.freeReturnBody : copy.signInNote}</p>
              <p><strong>{firebaseUser ? copy.payNow : copy.afterTrial}</strong></p>
            </aside>
          )}
          <div>
            <p className="puzzle-offer-eyebrow">{copy.offerEyebrow}</p>
            <h2>{copy.offerTitle}</h2>
            <p>{copy.offerBody}</p>
            <ul className="puzzle-offer-benefits">{copy.benefits.map((benefit) => <li key={benefit}><span aria-hidden="true">✓</span> {benefit}</li>)}</ul>
            <div className="puzzle-offer-coach">
              <h3>{copy.coachTitle}</h3>
              <p>{copy.coachBody}</p>
            </div>
            <small>{copy.only}</small>
          </div>
          <div className="puzzle-offer-membership-action">
            {trialActive && <p className="puzzle-offer-after-trial">{copy.afterTrial}</p>}
            <div className="puzzle-offer-price"><strong>£0.90</strong> <span>{copy.offerPrice}</span></div>
            {unlimited ? (
              <>
                <button type="button" className="puzzle-offer-button" onClick={beginTraining} disabled={!accessReady}>▶ {copy.fullButton}</button>
                {!testingPuzzleMembership && currentAccess?.canManage && <button type="button" className="puzzle-offer-text-button" onClick={manage} disabled={busy}>{copy.manage}</button>}
              </>
            ) : (
              <button type="button" className="puzzle-offer-button" onClick={firebaseUser ? subscribe : beginTraining} disabled={busy || !accessReady}>
                {!firebaseUser ? copy.guestButton : busy ? copy.processing : testingPuzzleMembership ? 'QA: activate £0.90 membership (no charge)' : copy.offerButton}
              </button>
            )}
            <small>{!firebaseUser || trialActive ? copy.signInNote : copy.cancel}</small>
            {(!firebaseUser || trialActive) && <small>{copy.cancel}</small>}
            {!firebaseUser && <Link to="/login" className="puzzle-offer-signin">{copy.login}</Link>}
            {params.get('billing') === 'success' && <small role="status">{copy.success}</small>}
            {params.get('billing') === 'cancelled' && <small role="status">{copy.cancelled}</small>}
          </div>
        </section>
      </div>
    </main>
  );
}


